import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { removeFileDocuments, selectAddFilesDocument, selectFileDocuments } from '../../features/actuarial/document-slice';
import { AddFileIconSVG, DeleteFileIconSVG } from '../../assets/icon/svg';
import { GetIconFile } from './get-icon-file';
import { IcRoundDownload } from '../../assets/icon/iconify';
import { useFilesActions } from '../../hooks/actions/use-files-actions';
import { useComputationActions } from '../../hooks/actions/user-computation-actions';

interface Props{
    isDisabled: boolean;
}

export default function DocumentList({ isDisabled }: Props) {

    const dispatch = useDispatch();
    const documentFiles = useSelector(selectFileDocuments);
    const { handleDownloadSupportingDocumentsAct } = useFilesActions()
    const { isLoadingUploadDocuments } = useComputationActions()
    const { isLoadingDownloadActDocuments } = useFilesActions()
    const isLoading = isLoadingDownloadActDocuments || isLoadingUploadDocuments
    
    const handleRemoveFile = (index: number) => {
        dispatch(removeFileDocuments(index));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = Array.from(e.target.files || []);
        dispatch(selectAddFilesDocument(selectedFiles));
        e.target.value = '';
    };

    const formatFileSize = (size: number) => {
        if (size < 1024) return `${size} B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
    };
    
    const sortedFiles = [...documentFiles].sort((a, b) => {
        if (a.type === b.type) return 0;
        return a.type === "new" ? -1 : 1;
    });

    return (
        <>
            {documentFiles?.length > 0 && (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-2">
                        <div className="flex items-center justify-between space-x-2">
                            {sortedFiles.length !== 0 && (
                                <div className="rounded bg-[#186cb2]/10 px-2 py-0.5 text-[11px] font-semibold text-[#186cb2]">
                                    {sortedFiles.length}
                                </div>
                            )}
                            <div className="text-xs font-semibold text-gray-700">
                                Attached Files
                            </div>
                        </div>
                        {isDisabled && (
                            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-[#186cb2] bg-white px-2 py-1.5 text-xs font-medium text-[#186cb2] transition hover:bg-blue-50">
                                <AddFileIconSVG size={15}/>
                                    Attach more files
                                <input
                                    type="file"
                                    multiple
                                    className="hidden"
                                    onChange={handleFileChange}
                                />
                            </label>
                        )}
                    </div>
                    <div className="divide-y divide-gray-100">
                        {sortedFiles?.map((file, index) => (
                            <div key={`${file.id}-${index}`} className="grid grid-cols-12 px-2 py-1.5 transition hover:bg-gray-50">
                                <div className="col-span-11 flex space-x-3 overflow-hidden truncate">
                                    <div className='col-span-1 h-full flex items-center'>
                                        <div className="flex h-7 w-7 items-center justify-center rounded bg-blue-100">
                                            {GetIconFile(file.fileName)}
                                        </div>
                                    </div>
                                    <div className="col-span-9 overflow-hidden flex items-center">
                                        <p className="truncate text-xs font-medium text-gray-700">
                                            {file.fileName}
                                        </p>
                                        {file.type === "new" && (
                                            <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-green-700">
                                                New
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className='col-span-1 flex items-center justify-end'>
                                    {file.type === 'new' && (
                                        <button
                                            type="button"
                                            disabled={!isDisabled || isLoading}
                                            onClick={() => handleRemoveFile(index)}
                                            className="disabled:cursor-not-allowed rounded-md p-2 text-red-500 transition hover:bg-red-50 hover:text-red-600"
                                        >
                                            <DeleteFileIconSVG size={20}/>
                                        </button>
                                    )}
                                    {file.type === 'old' && (
                                        <button
                                            type="button"
                                            disabled={isLoading}
                                            onClick={() => handleDownloadSupportingDocumentsAct(file.filePath, file.fileName)}
                                            className="rounded-md p-2 transition hover:bg-[#186cb2]/10"
                                        >
                                            <IcRoundDownload color='#186cb2' width={18} height={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    )
}
